import { Redirect, Route, RouteComponentProps, Switch } from "react-router-dom";
import PreviewArea from "src/Pages/Company/OrganizationStructure/Areas/PreviewArea";
import React, { useEffect } from "react";
import StructureSettings from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureSettings";
import StructureAreas from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureAreas/StructureAreas";
import StructureView from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView";
import useFetchListOfStructures from "src/Pages/Company/OrganizationStructure/api/useFetchListOfStructures";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { Loader } from "src/Components/Loader/Loader";
import { useStructureStore } from "src/Pages/Company/OrganizationStructure/context/useStructureStore";
import HeaderInfo from "src/Pages/Company/OrganizationStructure/sharedComponents/HeaderInfo";
import PreviewStructures from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs";
import CreateArea from "src/Pages/Company/OrganizationStructure/Areas/CreateArea";
import SecureRoute from "src/utils/RoleBasedSecurity/SecurityComponents/SecureRoute";
import {
  organizationStructureModuleRead,
  organizationStructureAreaAdd,
} from "src/utils/RoleBasedSecurity/permissions";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import __ from "src/utils/Translations";
import { useCompanyBreadcrumbs } from "src/Pages/Company/breadcrumbsHelper";
import { AxiosError } from "axios";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";
import Page404 from "src/utils/ErrorPages/Page404/Page404";

export default function OrganizationStructure({ match }: RouteComponentProps) {
  const companyId = useCompanyId();
  const logStatus = useLogStatus();
  const {
    data,
    isLoading,
    error: listError,
  } = useFetchListOfStructures(companyId);
  const structureId = data?.[0]?.structureId;
  const saveStructureId = useStructureStore((state) => state.setStructureId);

  if (listError) {
    const error = listError as AxiosError;
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "OrganizationStructure: fetchListOfStructures failed",
      statusDetails: {
        source: "API",
        operation: "fetchListOfStructures",
        status: error.response?.status,
        url: error.config?.url,
      },
    });
  }

  useEffect(() => {
    if (structureId) {
      saveStructureId(structureId);
    }
  }, [structureId, saveStructureId]);

  const breadcrumbs = useCompanyBreadcrumbs([
    {
      label: __("Struktura organizacji"),
      path: "/organization-structure",
    },
    {
      label: __("Konfiguracja struktury"),
      path: "/organization-structure/configure-structure",
    },
  ]);

  const HeadingInfo = () => (
    <>
      {structureId && <BreadcrumbsMui items={breadcrumbs} sx={{ mb: 4 }} />}
      <HeaderInfo companyId={companyId} structureId={structureId!} />
      <PreviewStructures />
    </>
  );

  return (
    <Switch>
      <SecureRoute
        permission={organizationStructureModuleRead}
        exact
        path={`${match.url}`}
        render={() => {
          if (isLoading || !companyId) return <Loader />;
          if (!structureId)
            return <Redirect to={`${match.url}/create-structure`} />;
          return <Redirect to={`${match.url}/structure-areas`} />;
        }}
      />
      <SecureRoute
        exact
        permission={organizationStructureModuleRead}
        path={[
          `${match.url}/structure-areas`,
          `${match.url}/structure-view/areas-organization-structure`,
          `${match.url}/structure-view/employees-organization-structure`,
          `${match.url}/structure-settings`,
        ]}
        render={(routeProps: RouteComponentProps) => {
          const { location } = routeProps;

          if (isLoading || !companyId) {
            return <Loader />;
          }

          return (
            <>
              <Switch location={location}>
                <SecureRoute
                  permission={organizationStructureModuleRead}
                  path={`${match.url}/structure-areas`}
                  render={(routeProps: RouteComponentProps) => (
                    <>
                      <HeadingInfo />
                      <StructureAreas
                        {...routeProps}
                        structureId={structureId!}
                      />
                    </>
                  )}
                />
                <SecureRoute
                  permission={organizationStructureModuleRead}
                  exact
                  path={`${match.url}/structure-view/:orgchartType`}
                  render={(routeProps: RouteComponentProps) => (
                    <>
                      <HeadingInfo />
                      <StructureView
                        {...routeProps}
                        structureId={structureId!}
                        companyId={companyId}
                      />
                    </>
                  )}
                />
                <SecureRoute
                  permission={organizationStructureModuleRead}
                  exact
                  path={`${match.url}/structure-view/:orgchartType/node/:nodeId`}
                  render={(routeProps: RouteComponentProps) => (
                    <>
                      <HeadingInfo />
                      <StructureView
                        {...routeProps}
                        structureId={structureId!}
                        companyId={companyId}
                      />
                    </>
                  )}
                />
                <SecureRoute
                  permission={organizationStructureModuleRead}
                  exact
                  path={`${match.url}/structure-settings`}
                  render={(routeProps: RouteComponentProps) => (
                    <>
                      <HeadingInfo />
                      <StructureSettings
                        {...routeProps}
                        structureId={structureId!}
                      />
                    </>
                  )}
                />
              </Switch>
            </>
          );
        }}
      />
      <SecureRoute
        permission={organizationStructureModuleRead}
        exact
        path={`${match.url}/area/:areaId`}
        render={() => (
          <>
            <BreadcrumbsMui items={breadcrumbs} sx={{ mb: 4 }} />
            <PreviewArea />
          </>
        )}
      />
      <SecureRoute
        permission={organizationStructureAreaAdd}
        exact
        path={`${match.url}/create-area`}
        render={() => (
          <>
            <BreadcrumbsMui items={breadcrumbs} sx={{ mb: 4 }} />
            <CreateArea />
          </>
        )}
      />
      <Route>
        <Page404 />
      </Route>
    </Switch>
  );
}
